import { useEffect, useMemo, useState } from "react";
import {
  createGatheringSchema,
  type CreateGatheringInput,
} from "@plotlounge/shared/validations";
import type {
  ClubReadingRoom,
  GatheringInput,
  GatheringVenueType,
} from "@/lib/clubs/gatherings";
import { PlaceAutocomplete } from "@/components/PlaceAutocomplete";
import styles from "./GatheringForm.module.scss";

const VENUE_TYPES: { value: GatheringVenueType; label: string }[] = [
  { value: "BOOKSTORE", label: "Bookstore" },
  { value: "CAFE", label: "Café" },
  { value: "LIBRARY", label: "Library" },
  { value: "PARK", label: "Park" },
  { value: "HOME", label: "Home" },
  { value: "OTHER", label: "Other" },
];

export interface GatheringFormInitialValues {
  title: string;
  description: string;
  /** Local date (YYYY-MM-DD). */
  dateLocal: string;
  /** Local start time (HH:mm). */
  timeLocal: string;
  /** Local end time (HH:mm) — empty when not set. */
  endTimeLocal: string;
  venueType: GatheringVenueType | "";
  venueName: string;
  neighborhood: string;
  address: string;
  capacity: string;
  discussionFocus: string;
  readingRoomLinkEnabled: boolean;
  readingRoomId: string;
  publiclyVisible: boolean;
}

export interface GatheringFormProps {
  mode: "create" | "edit";
  initialValues?: Partial<GatheringFormInitialValues>;
  readingRooms: ClubReadingRoom[];
  isReadingRoomsLoading?: boolean;
  isSubmitting?: boolean;
  isCancelling?: boolean;
  submitError?: Error | null;
  /**
   * When the parent club is staff-marked discoverable, the cockpit shows the
   * "List on /discover" checkbox. Otherwise the checkbox is hidden entirely
   * (it would be a no-op since the API forces publiclyVisible=false anyway).
   */
  clubDiscoverable?: boolean;
  /** Submit the gathering payload. Caller handles persistence + navigation. */
  onSubmit: (payload: GatheringInput) => void;
  /** Discard changes / leave the form. */
  onDiscard: () => void;
  /** Only set in edit mode. Triggers cancellation flow with confirmation. */
  onCancelGathering?: () => void;
}

const EMPTY_INITIAL: GatheringFormInitialValues = {
  title: "",
  description: "",
  dateLocal: "",
  timeLocal: "19:00",
  endTimeLocal: "",
  venueType: "",
  venueName: "",
  neighborhood: "",
  address: "",
  capacity: "",
  discussionFocus: "",
  readingRoomLinkEnabled: false,
  readingRoomId: "",
  // Default to true at create — mirrors club state at create time when the
  // club is discoverable. The form only renders the checkbox when
  // clubDiscoverable is true, so this default is effectively only used then.
  publiclyVisible: true,
};

/**
 * Shared gathering form module — used by both the create and edit routes.
 *
 * Pure-ish presentational: takes initial values, surfaces submit/discard/cancel
 * callbacks. The parent route owns persistence (mutations) and navigation.
 *
 * Validation runs client-side via the shared `createGatheringSchema`. The form
 * combines local date/time fields into an ISO datetime before calling onSubmit.
 */
export function GatheringForm({
  mode,
  initialValues,
  readingRooms,
  isReadingRoomsLoading = false,
  isSubmitting = false,
  isCancelling = false,
  submitError,
  clubDiscoverable = false,
  onSubmit,
  onDiscard,
  onCancelGathering,
}: GatheringFormProps) {
  const initial = useMemo(
    () => ({ ...EMPTY_INITIAL, ...initialValues }),
    [initialValues],
  );

  const [values, setValues] = useState<GatheringFormInitialValues>(initial);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof CreateGatheringInput | "time", string>>
  >({});

  useEffect(() => {
    setValues(initial);
    setFieldErrors({});
  }, [initial]);

  const set = <K extends keyof GatheringFormInitialValues>(
    key: K,
    value: GatheringFormInitialValues[K],
  ) => setValues((v) => ({ ...v, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = buildPayload(values, { clubDiscoverable });
    if (!payload.ok) {
      setFieldErrors(payload.errors);
      return;
    }
    setFieldErrors({});
    onSubmit(payload.data);
  };

  const isCreate = mode === "create";

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="g-title">
          Title <span className={styles.required}>*</span>
        </label>
        <input
          id="g-title"
          className={styles.input}
          type="text"
          value={values.title}
          onChange={(e) => set("title", e.target.value)}
          placeholder="May meetup — chapters 4–7"
          maxLength={150}
          disabled={isSubmitting}
          aria-invalid={Boolean(fieldErrors.title)}
          aria-describedby={fieldErrors.title ? "g-title-err" : undefined}
        />
        {fieldErrors.title && (
          <p id="g-title-err" className={styles.error}>
            {fieldErrors.title}
          </p>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="g-desc">
          Description
        </label>
        <textarea
          id="g-desc"
          className={styles.textarea}
          value={values.description}
          onChange={(e) => set("description", e.target.value)}
          rows={3}
          maxLength={1000}
          disabled={isSubmitting}
          placeholder="What members should know before showing up."
        />
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="g-date">
            Date <span className={styles.required}>*</span>
          </label>
          <input
            id="g-date"
            className={styles.input}
            type="date"
            value={values.dateLocal}
            onChange={(e) => set("dateLocal", e.target.value)}
            disabled={isSubmitting}
            aria-invalid={Boolean(fieldErrors.date)}
            aria-describedby={fieldErrors.date ? "g-date-err" : undefined}
          />
          {fieldErrors.date && (
            <p id="g-date-err" className={styles.error}>
              {fieldErrors.date}
            </p>
          )}
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="g-time">
            Start time <span className={styles.required}>*</span>
          </label>
          <input
            id="g-time"
            className={styles.input}
            type="time"
            value={values.timeLocal}
            onChange={(e) => set("timeLocal", e.target.value)}
            disabled={isSubmitting}
            aria-invalid={Boolean(fieldErrors.time)}
            aria-describedby={fieldErrors.time ? "g-time-err" : undefined}
          />
          {fieldErrors.time && (
            <p id="g-time-err" className={styles.error}>
              {fieldErrors.time}
            </p>
          )}
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="g-endtime">
            End time
            <span className={styles.optional}>optional</span>
          </label>
          <input
            id="g-endtime"
            className={styles.input}
            type="time"
            value={values.endTimeLocal}
            onChange={(e) => set("endTimeLocal", e.target.value)}
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div className={styles.field}>
        <span className={styles.label}>Venue type</span>
        <div
          className={styles.chipRow}
          role="radiogroup"
          aria-label="Venue type"
        >
          {VENUE_TYPES.map((vt) => {
            const selected = values.venueType === vt.value;
            return (
              <button
                key={vt.value}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`${styles.chip} ${selected ? styles.chipActive : ""}`}
                onClick={() => set("venueType", selected ? "" : vt.value)}
                disabled={isSubmitting}
              >
                {vt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="g-venue">
            Venue name<span className={styles.optional}>optional</span>
          </label>
          <input
            id="g-venue"
            className={styles.input}
            type="text"
            value={values.venueName}
            onChange={(e) => set("venueName", e.target.value)}
            maxLength={200}
            disabled={isSubmitting}
            placeholder="Greenlight Bookstore"
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="g-neigh">
            Neighborhood<span className={styles.optional}>optional</span>
          </label>
          <PlaceAutocomplete
            inputId="g-neigh"
            value={values.neighborhood}
            onChange={(text) => set("neighborhood", text.slice(0, 100))}
            onSelect={(place) => set("neighborhood", place.text.slice(0, 100))}
            disabled={isSubmitting}
            placeholder="Fort Greene"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="g-addr">
          Address<span className={styles.optional}>optional</span>
        </label>
        <input
          id="g-addr"
          className={styles.input}
          type="text"
          value={values.address}
          onChange={(e) => set("address", e.target.value)}
          maxLength={500}
          disabled={isSubmitting}
          placeholder="686 Fulton St, Brooklyn, NY"
        />
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="g-cap">
            Capacity<span className={styles.optional}>optional</span>
          </label>
          <input
            id="g-cap"
            className={styles.input}
            type="number"
            min={2}
            max={500}
            value={values.capacity}
            onChange={(e) => set("capacity", e.target.value)}
            disabled={isSubmitting}
            placeholder="12"
            aria-invalid={Boolean(fieldErrors.capacity)}
            aria-describedby={fieldErrors.capacity ? "g-cap-err" : undefined}
          />
          {fieldErrors.capacity && (
            <p id="g-cap-err" className={styles.error}>
              {fieldErrors.capacity}
            </p>
          )}
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="g-focus">
            Discussion focus<span className={styles.optional}>optional</span>
          </label>
          <input
            id="g-focus"
            className={styles.input}
            type="text"
            value={values.discussionFocus}
            onChange={(e) => set("discussionFocus", e.target.value)}
            maxLength={500}
            disabled={isSubmitting}
            placeholder="Chapters 4–7"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={values.readingRoomLinkEnabled}
            onChange={(e) => {
              set("readingRoomLinkEnabled", e.target.checked);
              if (!e.target.checked) set("readingRoomId", "");
            }}
            disabled={isSubmitting}
          />
          <span>Tie this gathering to a reading room</span>
        </label>
        {values.readingRoomLinkEnabled && (
          <select
            className={styles.input}
            aria-label="Reading room"
            value={values.readingRoomId}
            onChange={(e) => set("readingRoomId", e.target.value)}
            disabled={isSubmitting || isReadingRoomsLoading}
          >
            <option value="">
              {isReadingRoomsLoading
                ? "Loading rooms…"
                : readingRooms.length === 0
                  ? "No reading rooms yet"
                  : "Select a reading room…"}
            </option>
            {readingRooms.map((r) => (
              <option key={r.id} value={r.id}>
                {r.book?.title ?? r.name ?? r.id}
              </option>
            ))}
          </select>
        )}
      </div>

      {clubDiscoverable && (
        <div className={styles.field}>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={values.publiclyVisible}
              onChange={(e) => set("publiclyVisible", e.target.checked)}
              disabled={isSubmitting}
            />
            <span>List this gathering on /discover</span>
          </label>
          <p className={styles.hint}>
            When on, anyone can find this gathering on the public city feed and
            RSVP without an account.
          </p>
        </div>
      )}

      {submitError && (
        <p className={styles.errorBanner} role="alert">
          {submitError.message || "Something went wrong."}
        </p>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.btnGhost}
          onClick={onDiscard}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={styles.btnPrimary}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? isCreate
              ? "Scheduling…"
              : "Saving…"
            : isCreate
              ? "Schedule gathering"
              : "Save changes"}
        </button>
      </div>

      {mode === "edit" && onCancelGathering && (
        <div className={styles.dangerZone}>
          <h3 className={styles.dangerTitle}>Cancel this gathering</h3>
          <p className={styles.dangerText}>
            Attendees will be notified and the gathering will be marked as
            cancelled. This cannot be undone.
          </p>
          <button
            type="button"
            className={styles.btnDanger}
            onClick={() => {
              if (
                window.confirm(
                  "Cancel this gathering? Attendees will be notified.",
                )
              ) {
                onCancelGathering();
              }
            }}
            disabled={isSubmitting || isCancelling}
          >
            {isCancelling ? "Cancelling…" : "Cancel gathering"}
          </button>
        </div>
      )}
    </form>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

interface PayloadOk {
  ok: true;
  data: GatheringInput;
}
interface PayloadErr {
  ok: false;
  errors: Partial<Record<keyof CreateGatheringInput | "time", string>>;
}

function combineLocalDateTime(
  dateLocal: string,
  timeLocal: string,
): string | null {
  if (!dateLocal || !timeLocal) return null;
  // Construct as local datetime then convert to ISO with the browser's tz offset.
  const dt = new Date(`${dateLocal}T${timeLocal}:00`);
  if (isNaN(dt.getTime())) return null;
  return dt.toISOString();
}

function buildPayload(
  values: GatheringFormInitialValues,
  options: { clubDiscoverable?: boolean } = {},
): PayloadOk | PayloadErr {
  const errors: PayloadErr["errors"] = {};

  const title = values.title.trim();
  if (!title) errors.title = "Title is required";

  const dateIso = combineLocalDateTime(values.dateLocal, values.timeLocal);
  if (!values.dateLocal) errors.date = "Date is required";
  if (!values.timeLocal) errors.time = "Start time is required";
  if (values.dateLocal && values.timeLocal && !dateIso)
    errors.date = "Invalid date or time";

  let endIso: string | undefined;
  if (values.endTimeLocal) {
    const combined = combineLocalDateTime(
      values.dateLocal,
      values.endTimeLocal,
    );
    if (combined) endIso = combined;
  }

  let capacityNum: number | undefined;
  if (values.capacity.trim()) {
    const parsed = Number(values.capacity);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
      errors.capacity = "Capacity must be a whole number";
    } else if (parsed < 2) {
      errors.capacity = "Capacity must be at least 2";
    } else if (parsed > 500) {
      errors.capacity = "Capacity must be 500 or less";
    } else {
      capacityNum = parsed;
    }
  }

  if (Object.keys(errors).length > 0 || !dateIso) {
    return { ok: false, errors };
  }

  const candidate: GatheringInput = {
    title,
    description: values.description.trim() || undefined,
    date: dateIso,
    endDate: endIso,
    venueType: values.venueType || undefined,
    venueName: values.venueName.trim() || undefined,
    neighborhood: values.neighborhood.trim() || undefined,
    address: values.address.trim() || undefined,
    capacity: capacityNum,
    discussionFocus: values.discussionFocus.trim() || undefined,
    readingRoomId:
      values.readingRoomLinkEnabled && values.readingRoomId
        ? values.readingRoomId
        : undefined,
    publiclyVisible: options.clubDiscoverable
      ? values.publiclyVisible
      : undefined,
  };

  // Final cross-check via the shared schema, in case a constraint slipped past.
  const parsed = createGatheringSchema.safeParse(candidate);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const path = issue.path[0] as keyof CreateGatheringInput | undefined;
    return {
      ok: false,
      errors: path ? { [path]: issue.message } : { title: issue.message },
    };
  }

  return { ok: true, data: parsed.data };
}

// Exported for tests.
export const __test__ = { buildPayload, combineLocalDateTime };
